/**
 * Menu Filtering Utility
 * Filters menu items based on modules from subscription API
 */

/**
 * Parse stringified module array from API
 * @param {string|Array} stringifiedModules - Stringified array or array
 * @returns {Array} Parsed array of module strings
 */
export const parseModules = (stringifiedModules) => {
  try {
    if (!stringifiedModules) return [];
    if (Array.isArray(stringifiedModules)) return stringifiedModules;

    const modules = JSON.parse(stringifiedModules);
    return Array.isArray(modules) ? modules : [];
  } catch (error) {
    console.error("Failed to parse modules:", error);
    return [];
  }
};

/**
 * Filter modules to only include those starting with "dashboard."
 * @param {Array} modules - Array of module strings
 * @returns {Array} Filtered modules
 */
export const filterDashboardModules = (modules) => {
  if (!Array.isArray(modules)) return [];
  return modules.filter((module) => typeof module === "string" && module.startsWith("dashboard."));
};

/**
 * Extract all modules from subscription services array
 * @param {Array} services - Services array from /subscription/info API
 * @returns {Array} Array of dashboard module strings
 *
 * @example
 * const services = [
 *   { modules: '["dashboard.mainmenu.business_profile", "dashboard.social.metaverse"]' }
 * ];
 * extractModulesFromServices(services);
 * // Returns: ["dashboard.mainmenu.business_profile", "dashboard.social.metaverse"]
 */
export const extractModulesFromServices = (services) => {
  if (!Array.isArray(services)) {
    console.warn("extractModulesFromServices: services is not an array");
    return [];
  }

  const allModules = services.flatMap((service) => {
    if (service.modules) {
      return parseModules(service.modules);
    }
    return [];
  });

  return filterDashboardModules(allModules);
};

/**
 * Normalize string for comparison (remove spaces, underscores, lowercase)
 */
const normalizeString = (str) => str.toLowerCase().replace(/[\s_-]/g, "");

/**
 * Mapping from menu item keys to their corresponding API module segment names.
 * Only needed for keys that don't directly match the API module's last segment.
 * Each entry maps a menu key to an array of acceptable module segment names.
 */
const MENU_KEY_MODULE_OVERRIDES = {
  // Main Menu > Business Profile
  add_branch: ["create_branch"],
  configure_taxation: ["taxation"],

  // Main Menu > Categories
  update_category: ["manage_category"],

  // Main Menu > Admin
  create_user: ["create_admin"],
  manage_employees: ["manage_admin"],
  manage_modules: ["package_management"],
  manage_roles: ["service_managment"],

  // Social > Metaverse
  manage_advertisement: ["advertisement", "create_advertisement"],
  brand_hosted_events: ["brand_hosted_event", "create_brand_hosted_event"],
  influencer_hosted_events: ["influencer_hosted_event", "create_influencer_hosted_event"],
  events_requests: ["influencer_request_for_hosting"],

  // Social > Loyalty Campaigns
  create_compensation: ["manage_compensation"],
  manage_quests: ["manage_compensation"],
  create_loyality_campaign: ["manage_compensation"],
  payout_history: ["manage_compensation"],

  // Social > For You
  reviews_requests: ["review_requests"],

  // Social > Ambassadors
  Ambassadors_applied: ["ambassador"],
  manage_ambassadors: ["ambassador"],

  // E-commerce > 3D Product
  create_3D_product: ["manage_3d_product", "3d_product"],
  manage_3D_products: ["manage_3d_product"],

  // E-commerce > Products
  import_products_images: ["import_product_images"],
  create_product: ["manage_product"],
  manage_products: ["manage_product"],
  create_attribute: ["manage_attribute"],
  manage_attributes: ["manage_attribute"],
  product_reviews: ["manage_product"],

  // E-commerce > Tags
  create_tag: ["manage_tags"],
  tags_tree_preview: ["tag_tree_preview"],

  // E-commerce > 360 Stores
  create_360_stores: ["manage_360_stores"],

  // E-commerce > Loyalty
  create_tier: ["manage_tiers"],
  create_configuration: ["manage_configuration"],
  manage_configurations: ["manage_configuration"],
  manage_wallet_pass: ["loyalty"],
  wallet_pass_notifications: ["loyalty"],
  configure_referral_url: ["loyalty"],
  shop_checkin_rewards_qr: ["loyalty"],
  points_redemption: ["loyalty", "manage_tiers"],

  // E-commerce > Promotions
  create_promotion: ["manage_promotions"],

  // E-commerce > Gift Cards
  gift_card_products: ["giftcards"],
  gift_card_add_edit: ["giftcards"],
  issue_gift_card: ["giftcards"],
  gift_card_analytics: ["giftcards"],
  gift_card_details: ["giftcards"],

  // E-commerce
  billing_and_addon: ["billing_details"],
  Scan_for_pick: ["scan_qr_code"],

  // Orders > Shipping
  create_shipping_service: ["manage_shipping"],
  manage_shipping_service: ["manage_shipping"],

  // Customer Support
  booking_calls: ["video_calls"],
  customers: ["customer"],
};

/**
 * Check if a permission module matches a menu key.
 * Splits the permission into dot-separated segments and checks for exact
 * segment matches (after normalization) against the key or its overrides.
 */
const matchesKey = (permission, key) => {
  const segments = permission.split(".").map(normalizeString);
  const normalizedKey = normalizeString(key);

  // Check 1: exact segment match with the key itself
  if (segments.some((segment) => segment === normalizedKey)) {
    return true;
  }

  // Check 2: check override map for alternative module segment names
  const overrides = MENU_KEY_MODULE_OVERRIDES[key];
  if (overrides) {
    return overrides.some((override) => {
      const normalizedOverride = normalizeString(override);
      return segments.some((segment) => segment === normalizedOverride);
    });
  }

  return false;
};

/**
 * Filter menu by permissions using key matching
 * This is the main filtering function that matches permissions to menu item keys
 *
 * @param {Array} menuConfig - Complete menu structure
 * @param {Array} permissions - Array of permission strings (modules from API)
 * @returns {Array} Filtered menu structure
 *
 * @example
 * const permissions = ['dashboard.social.metaverse.manage_advertisement'];
 * const filtered = filterMenuByPermissions(menuConfig, permissions);
 */
export const filterMenuByPermissions = (menuConfig, permissions) => {
  console.log("🔍 Filtering menu with permissions:", permissions);

  return menuConfig
    .map((group) => {
      const filteredGroupList = group.groupList
        .map((menu) => {
          // CASE 1: menu has children
          if (menu.child && menu.child.length > 0) {
            console.log(`  Checking parent "${menu.key}" with ${menu.child.length} children`);

            const allowedChildren = menu.child.filter((child) => {
              const hasAccess = permissions.some((permission) => matchesKey(permission, child.key));

              // if (hasAccess) {
              //   console.log(`    ✅ "${child.key}" - ALLOWED`);
              // } else {
              //   console.log(`    ❌ "${child.key}" - DENIED (no matching permission)`);
              // }

              return hasAccess;
            });

            if (allowedChildren.length > 0) {
              console.log(
                `  ✅ Parent "${menu.key}" included with ${allowedChildren.length} children`,
              );
              return {
                ...menu,
                child: allowedChildren,
              };
            }

            console.log(`  ❌ Parent "${menu.key}" excluded - no matching children`);
            return null;
          }

          // CASE 2: menu without children
          const hasPermission = permissions.some((permission) => matchesKey(permission, menu.key));

          if (hasPermission) {
            console.log(`  ✅ "${menu.key}" - ALLOWED`);
          } else {
            console.log(`  ❌ "${menu.key}" - DENIED`);
          }

          return hasPermission ? menu : null;
        })
        .filter(Boolean);

      if (filteredGroupList.length > 0) {
        console.log(
          `✅ Group "${group.groupTitle}" included with ${filteredGroupList.length} items`,
        );
        return {
          ...group,
          groupList: filteredGroupList,
        };
      }

      console.log(`❌ Group "${group.groupTitle}" excluded`);
      return null;
    })
    .filter(Boolean);
};

/**
 * Check if user has ALL_UNLOCKED service (grants access to everything)
 * @param {Array} services - Services from /subscription/info API
 * @returns {boolean} True if ALL_UNLOCKED service is present
 */
export const hasAllUnlockedAccess = (services) => {
  if (!Array.isArray(services)) return false;
  return services.some((service) => service.code && service.code.toLowerCase() === "all_unlocked");
};

/**
 * Main function to filter menu based on modules from subscription API
 * Combines module extraction with permission-based filtering
 *
 * @param {Array} menuData - Complete menu structure
 * @param {Array} services - Services from /subscription/info API
 * @returns {Array} Filtered menu structure
 *
 * @example
 * const menuData = [...]; // Your menu structure
 * const services = [
 *   { modules: '["dashboard.mainmenu.business_profile", "dashboard.social.metaverse"]' }
 * ];
 * const filteredMenu = filterMenuByModules(menuData, services);
 */
export const filterMenuByModules = (menuData, services) => {
  // Check if user has ALL_UNLOCKED service - grants full access
  if (hasAllUnlockedAccess(services)) {
    console.log("🔓 ALL_UNLOCKED service detected - granting full menu access");
    return menuData;
  }

  // Extract modules from services
  const userModules = extractModulesFromServices(services);

  console.log("Menu filtering - Extracted modules:", userModules);

  if (userModules.length === 0) {
    console.warn("No modules found in services, returning empty menu");
    return [];
  }

  // Use permission-based filtering with modules as permissions
  const filteredMenu = filterMenuByPermissions(menuData, userModules);

  console.log("Menu filtering - Filtered menu groups:", filteredMenu.length);

  return filteredMenu;
};

/**
 * Filter menu by raw module array (already extracted)
 * @param {Array} menuData - Complete menu structure
 * @param {Array} modules - Array of module strings
 * @returns {Array} Filtered menu structure
 */
export const filterMenuByModuleArray = (menuData, modules) => {
  const dashboardModules = filterDashboardModules(modules);

  console.log("Menu filtering - Using modules:", dashboardModules);

  if (dashboardModules.length === 0) {
    console.warn("No dashboard modules provided, returning empty menu");
    return [];
  }

  const filteredMenu = filterMenuByPermissions(menuData, dashboardModules);

  console.log("Menu filtering - Filtered menu groups:", filteredMenu.length);

  return filteredMenu;
};

export default {
  parseModules,
  filterDashboardModules,
  extractModulesFromServices,
  filterMenuByPermissions,
  filterMenuByModules,
  filterMenuByModuleArray,
  hasAllUnlockedAccess,
};
